import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function PortalGlobalReservas() {
  const [edificios, setEdificios] = useState([]);
  const [reservasGlobales, setReservasGlobales] = useState([]);
  const [cargando, setCargando] = useState(true);

  // 🔍 Estados para filtros
  const [filtroEdificio, setFiltroEdificio] = useState('');
  const [filtroAnio, setFiltroAnio] = useState('');
  const [filtroMes, setFiltroMes] = useState('');

  // 📥 Cargar datos de todos los edificios y sus respectivas reservas y áreas en paralelo
  const cargarDatosGlobales = async () => {
    try {
      setCargando(true);
      const edifSnapshot = await getDocs(collection(db, 'edificios_mmdl'));
      const listaEdificios = edifSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Ejecutar las consultas de todos los edificios en paralelo simultáneamente
      const promesasDatos = listaEdificios.map(async (edifData) => {
        const [areasSnap, reservasSnap] = await Promise.all([
          getDocs(collection(db, `areas_${edifData.id}`)),
          getDocs(collection(db, `reservas_${edifData.id}`))
        ]);

        const areasMap = {};
        areasSnap.docs.forEach(aDoc => {
          areasMap[aDoc.id] = aDoc.data();
        });

        const reservasEdificio = [];
        reservasSnap.docs.forEach(rDoc => {
          const rData = rDoc.data();
          const areaObj = areasMap[rData.areaId] || {};
          reservasEdificio.push({
            id: rDoc.id,
            edificioId: edifData.id,
            ...rData,
            areaNombre: areaObj.nombre || 'Área Social',
            costoArea: areaObj.costo || '0',
            garantiaArea: areaObj.garantia || '0'
          });
        });

        return reservasEdificio;
      });

      const resultadosPorEdificio = await Promise.all(promesasDatos);
      const todasLasReservas = resultadosPorEdificio.flat();

      setEdificios(listaEdificios);
      setReservasGlobales(todasLasReservas);
    } catch (error) {
      console.error("Error al cargar el reporte global de reservas:", error);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDatosGlobales();
  }, []);

  // 🧮 Utilidad para limpiar y convertir valores a número
  const obtenerValorNumerico = (valorStr) => {
    if (valorStr === undefined || valorStr === null || valorStr === '') return 0;
    if (typeof valorStr === 'number') return valorStr;
    const limpio = valorStr.toString().replace(/[^0-9.]/g, '');
    const num = parseFloat(limpio);
    return isNaN(num) ? 0 : num;
  };

  // 🔄 Función inteligente para retornar o anular la devolución de garantía global (Toggle)
  const retornarGarantiaGrupoGlobal = async (grupo) => {
    const valorGarantia = obtenerValorNumerico(grupo.garantiaArea);

    if (valorGarantia <= 0) {
      alert("⚠️ Esta reserva no cuenta con un monto de garantía aplicable.");
      return;
    }

    const yaDevuelta = grupo.estadoGarantia === 'Devuelta';

    const mensaje = yaDevuelta
      ? `¿Deseas ANULAR la devolución de garantía por S/ ${valorGarantia.toFixed(2)} para el Dpto. ${grupo.departamento} (${grupo.edificioId})? El monto volverá a sumarse al total recaudado.`
      : `¿Estás seguro de registrar la devolución de la garantía por S/ ${valorGarantia.toFixed(2)} para el Dpto. ${grupo.departamento} (${grupo.edificioId})? Esto se descontará del total recaudado.`;

    if (window.confirm(mensaje)) {
      try {
        for (let id of grupo.ids) {
          await updateDoc(doc(db, `reservas_${grupo.edificioId}`, id), {
            estadoGarantia: yaDevuelta ? '' : 'Devuelta'
          });
        }

        await cargarDatosGlobales();

        alert(yaDevuelta ? "✅ Devolución anulada. El fondo ha sido restaurado." : "✅ Garantía devuelta y descontada del total con éxito.");
      } catch (error) {
        console.error("Error al modificar garantía global:", error);
        alert("❌ Hubo un error al actualizar el estado de la garantía.");
      }
    }
  };

  // ❌ Función para alternar si la garantía no fue pagada en el portal global
  const toggleGarantiaNoPagadaGlobal = async (grupo) => {
    const valorGarantia = obtenerValorNumerico(grupo.garantiaArea);

    if (valorGarantia <= 0) {
      alert("⚠️ Esta reserva no cuenta con un monto de garantía aplicable.");
      return;
    }

    const noPagadaActual = grupo.estadoGarantia === 'No pagada';

    const mensaje = noPagadaActual
      ? `¿Deseas marcar la garantía como PAGADA para el Dpto. ${grupo.departamento} (${grupo.edificioId})? El monto volverá a sumarse al total recaudado.`
      : `¿Deseas marcar la garantía como NO PAGADA para el Dpto. ${grupo.departamento} (${grupo.edificioId})? El monto dejará de sumarse al total recaudado.`;

    if (window.confirm(mensaje)) {
      try {
        const nuevoEstado = noPagadaActual ? '' : 'No pagada';

        for (let id of grupo.ids) {
          await updateDoc(doc(db, `reservas_${grupo.edificioId}`, id), {
            estadoGarantia: nuevoEstado
          });
        }

        await cargarDatosGlobales();

        alert(noPagadaActual ? "✅ Garantía marcada como pagada. Se ha vuelto a sumar al total." : "✅ Garantía marcada como no pagada. Se excluyó del total recaudado.");
      } catch (error) {
        console.error("Error al actualizar estado de garantía global:", error);
        alert("❌ Hubo un error al actualizar el estado de la garantía.");
      }
    }
  };

  // 📦 Agrupación lógica de reservas múltiples por lote
  const reservasAgrupadasMap = {};
  reservasGlobales.forEach(r => {
    const key = `${r.edificioId}_${r.areaId}_${r.fecha}_${r.departamento}_${r.voucherUrl || ''}`;
    if (!reservasAgrupadasMap[key]) {
      reservasAgrupadasMap[key] = {
        ids: [r.id],
        edificioId: r.edificioId,
        areaId: r.areaId,
        areaNombre: r.areaNombre,
        costoArea: r.costoArea,
        garantiaArea: r.garantiaArea,
        departamento: r.departamento,
        fecha: r.fecha,
        horarios: [r.horario],
        voucherUrl: r.voucherUrl,
        voucherGarantiaUrl: r.voucherGarantiaUrl,
        estadoGarantia: r.estadoGarantia
      };
    } else {
      reservasAgrupadasMap[key].ids.push(r.id);
      reservasAgrupadasMap[key].horarios.push(r.horario);
      if (r.voucherGarantiaUrl && !reservasAgrupadasMap[key].voucherGarantiaUrl) {
        reservasAgrupadasMap[key].voucherGarantiaUrl = r.voucherGarantiaUrl;
        reservasAgrupadasMap[key].estadoGarantia = r.estadoGarantia;
      }
    }
  });

  const listaReservasAgrupadas = Object.values(reservasAgrupadasMap).map(grupo => {
    const hoursList = grupo.horarios.map(h => parseInt(h.split(':')[0]) || 0);
    const hasEvening = hoursList.some(h => h >= 18);

    const horariosOrdenados = [...grupo.horarios].sort((a, b) => {
      const hA_raw = parseInt(a.split(':')[0]) || 0;
      const hB_raw = parseInt(b.split(':')[0]) || 0;

      let hA = hA_raw;
      let hB = hB_raw;

      if (hasEvening) {
        if (hA < 6) hA += 24;
        if (hB < 6) hB += 24;
      }

      return hA - hB;
    });

    let horarioMostrado = '';
    
    if (horariosOrdenados.length === 1) {
      horarioMostrado = horariosOrdenados[0];
    } else {
      const primerHorario = horariosOrdenados[0];
      const ultimoHorario = horariosOrdenados[horariosOrdenados.length - 1];
      const partesPrimero = primerHorario.split('-');
      const partesUltimo = ultimoHorario.split('-');
      if (partesPrimero.length === 2 && partesUltimo.length === 2) {
        horarioMostrado = `${partesPrimero[0].trim()} - ${partesUltimo[1].trim()}`;
      } else {
        horarioMostrado = `${primerHorario} al ${ultimoHorario}`;
      }
    }
    return {
      ...grupo,
      horarioMostrado
    };
  });

  // 🔍 Filtrar reservas por Edificio, Año y Mes
  const reservasFiltradas = listaReservasAgrupadas.filter(grupo => {
    const cumpleEdificio = filtroEdificio ? grupo.edificioId === filtroEdificio : true;
    
    if (!grupo.fecha) return cumpleEdificio;
    const [anioRes, mesRes] = grupo.fecha.split('-');
    const cumpleAnio = filtroAnio ? anioRes === filtroAnio : true;
    const cumpleMes = filtroMes ? mesRes === filtroMes : true;

    return cumpleEdificio && cumpleAnio && cumpleMes;
  });

  // 📅 Años disponibles para el filtro
  const aniosDisponibles = Array.from(
    new Set(reservasGlobales.map(r => (r.fecha ? r.fecha.split('-')[0] : '')))
  ).filter(Boolean).sort().reverse();

  // 💰 Cálculos financieros globales / filtrados
  const totalRecaudadoAlquileres = reservasFiltradas.reduce((acc, grupo) => {
    return acc + obtenerValorNumerico(grupo.costoArea);
  }, 0);

  // 🔄 CÁLCULO AUTOMÁTICO DE GARANTÍAS NETAS: Excluye no pagadas y devueltas
  const totalGarantiasGlobal = reservasFiltradas.reduce((acc, grupo) => {
    const valorGarantia = obtenerValorNumerico(grupo.garantiaArea);
    if (grupo.estadoGarantia === 'No pagada' || grupo.estadoGarantia === 'Devuelta') {
      return acc;
    }
    return acc + valorGarantia;
  }, 0);

  const totalRecaudadoBruto = totalRecaudadoAlquileres + totalGarantiasGlobal;

  // Monto inicial según el filtro de edificio actual
  const montoInicialBase = filtroEdificio 
    ? obtenerValorNumerico(edificios.find(e => e.id === filtroEdificio)?.montoInicialReservas || 0)
    : edificios.reduce((acc, e) => acc + obtenerValorNumerico(e.montoInicialReservas), 0);

  const granTotalAcumulado = montoInicialBase + totalRecaudadoBruto;

  // 🗑️ Función para liberar / cancelar reservas si el voucher no es válido
  const liberarGrupoGlobal = async (edificioId, ids) => {
    if (window.confirm("⚠️ ¿Está seguro de liberar estos horarios? Se eliminará la reserva y los horarios quedarán libres nuevamente.")) {
      try {
        for (let id of ids) {
          await deleteDoc(doc(db, `reservas_${edificioId}`, id));
        }
        setReservasGlobales(prev => prev.filter(r => !ids.includes(r.id)));
        alert("🗑️ Reserva(s) liberada(s) correctamente.");
      } catch (error) {
        console.error("Error al liberar reservas:", error);
        alert("❌ Hubo un error al intentar liberar los horarios.");
      }
    }
  };

  // 📥 Función para exportar a Excel / CSV
  const descargarExcelGlobal = () => {
    if (reservasFiltradas.length === 0) {
      alert("⚠️ No hay reservas registradas para exportar con los filtros seleccionados.");
      return;
    }

    let csvContent = "\uFEFF"; 
    csvContent += "REPORTE GLOBAL DE RESERVAS Y FONDO DE CAJA CHICA - MMD&L EDIFICIOS S.A.C.\n";
    csvContent += `Filtro Edificio:,${filtroEdificio || 'Todos los edificios'}\n`;
    csvContent += `Monto Inicial / Fondo Anterior (S/.):,${montoInicialBase.toFixed(2)}\n`;
    csvContent += `Total Recaudado en Alquileres (S/.):,${totalRecaudadoAlquileres.toFixed(2)}\n`;
    csvContent += `Total en Garantías Neto (S/.):,${totalGarantiasGlobal.toFixed(2)}\n`;
    csvContent += `Gran Total Acumulado en Caja Chica (S/.):,${granTotalAcumulado.toFixed(2)}\n\n`;
    
    csvContent += "Edificio,Área Social,Departamento,Fecha,Horario,Costo (S/),Garantía (S/),Tipo de Pago,Voucher Garantía\n";

    reservasFiltradas.forEach(grupo => {
      const edif = `"${grupo.edificioId}"`;
      const nombreArea = `"${(grupo.areaNombre || '').replace(/"/g, '""')}"`;
      const dpto = `"Dpto. ${grupo.departamento}"`;
      const fecha = `"${grupo.fecha || ''}"`;
      const horario = `"${grupo.horarioMostrado || ''}"`;
      const costoNum = obtenerValorNumerico(grupo.costoArea).toFixed(2);
      const garantiaNum = grupo.estadoGarantia === 'No pagada' ? '"0.00 (No pagada)"' : obtenerValorNumerico(grupo.garantiaArea).toFixed(2);
      const pago = grupo.voucherUrl ? '"Con Voucher"' : '"Gratis / Sin costo"';
      const voucherGarantia = grupo.voucherGarantiaUrl ? '"Presentado"' : '"No presentado"';

      csvContent += `${edif},${nombreArea},${dpto},${fecha},${horario},${costoNum},${garantiaNum},${pago},${voucherGarantia}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Reporte_Global_Reservas_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (cargando) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>⏳ Cargando información global de reservas...</div>;
  }

  return (
    <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '25px', textAlign: 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: '0 0 5px 0', color: '#1e293b' }}>🌐 Portal Global de Reservas y Recaudación</h2>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>Vista consolidada y control de pagos para el equipo administrativo.</p>
        </div>

        <button 
          onClick={descargarExcelGlobal}
          style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          📥 Descargar Excel Global
        </button>
      </div>

      {/* 📊 TARJETAS DE RESUMEN Y CONTROL DE FONDO */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px', marginBottom: '20px' }}>
        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '15px' }}>
          <span style={{ fontSize: '0.8rem', color: '#334155', fontWeight: 'bold' }}>📥 Monto Inicial / Fondo Base:</span>
          <h3 style={{ margin: '5px 0 0 0', color: '#0f172a' }}>S/ {montoInicialBase.toFixed(2)}</h3>
        </div>
        <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '15px' }}>
          <span style={{ fontSize: '0.8rem', color: '#166534', fontWeight: 'bold' }}>💰 Recaudado (Alquileres + Garantías Netas):</span>
          <h3 style={{ margin: '5px 0 0 0', color: '#15803d' }}>S/ {totalRecaudadoBruto.toFixed(2)}</h3>
        </div>
        <div style={{ backgroundColor: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '8px', padding: '15px' }}>
          <span style={{ fontSize: '0.8rem', color: '#1e3a8a', fontWeight: 'bold' }}>📈 Gran Total Acumulado en Caja:</span>
          <h3 style={{ margin: '5px 0 0 0', color: '#1e40af' }}>S/ {granTotalAcumulado.toFixed(2)}</h3>
        </div>
      </div>

      {/* 🔍 FILTROS DE BÚSQUEDA */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <select 
          value={filtroEdificio} 
          onChange={e => setFiltroEdificio(e.target.value)} 
          style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', flex: '1', minWidth: '160px' }}
        >
          <option value="">🏢 Todos los Edificios</option>
          {edificios.map(edif => (
            <option key={edif.id} value={edif.id}>{edif.id}</option>
          ))}
        </select>

        <select 
          value={filtroAnio} 
          onChange={e => setFiltroAnio(e.target.value)} 
          style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
        >
          <option value="">📅 Todos los Años</option>
          {aniosDisponibles.map(anio => (
            <option key={anio} value={anio}>{anio}</option>
          ))}
        </select>

        <select 
          value={filtroMes} 
          onChange={e => setFiltroMes(e.target.value)} 
          style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
        >
          <option value="">📆 Todos los Meses</option>
          <option value="01">Enero</option>
          <option value="02">Febrero</option>
          <option value="03">Marzo</option>
          <option value="04">Abril</option>
          <option value="05">Mayo</option>
          <option value="06">Junio</option>
          <option value="07">Julio</option>
          <option value="08">Agosto</option>
          <option value="09">Septiembre</option>
          <option value="10">Octubre</option>
          <option value="11">Noviembre</option>
          <option value="12">Diciembre</option>
        </select>
      </div>

      {/* 📋 TABLA GLOBAL */}
      <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
        <table className="tabla-propietarios" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ padding: '10px' }}>Edificio</th>
              <th style={{ padding: '10px' }}>Área Social</th>
              <th style={{ padding: '10px' }}>Dpto</th>
              <th style={{ padding: '10px' }}>Fecha</th>
              <th style={{ padding: '10px' }}>Horario</th>
              <th style={{ padding: '10px' }}>Costo</th>
              <th style={{ padding: '10px' }}>Garantía</th>
              <th style={{ padding: '10px' }}>Voucher</th>
              <th style={{ padding: '10px' }}>Voucher Garantía</th>
              <th style={{ padding: '10px' }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {reservasFiltradas.length === 0 ? (
              <tr><td colSpan="10" style={{ textAlign: 'center', padding: '25px', color: '#666' }}>No se encontraron reservas con los filtros seleccionados.</td></tr>
            ) : (
              reservasFiltradas.map((grupo, idx) => {
                const costoNum = obtenerValorNumerico(grupo.costoArea);
                const garantiaNum = obtenerValorNumerico(grupo.garantiaArea);

                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '10px' }}><strong style={{ color: '#0284c7' }}>{grupo.edificioId}</strong></td>
                    <td style={{ padding: '10px' }}>{grupo.areaNombre}</td>
                    <td style={{ padding: '10px' }}>Dpto. {grupo.departamento}</td>
                    <td style={{ padding: '10px' }}>{grupo.fecha}</td>
                    <td style={{ padding: '10px' }}><span style={{ backgroundColor: '#fee2e2', color: '#dc2626', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold' }}>🔒 {grupo.horarioMostrado}</span></td>
                    <td style={{ padding: '10px' }}>
                      {costoNum > 0 ? (
                        <strong style={{ color: '#0284c7' }}>S/ {costoNum.toFixed(2)}</strong>
                      ) : (
                        <span style={{ color: '#16a34a', fontSize: '0.75rem', fontWeight: 'bold' }}>Gratis</span>
                      )}
                    </td>
                    <td style={{ padding: '10px' }}>
                      {garantiaNum > 0 ? (
                        <span style={{ color: '#d97706', fontWeight: 'bold', fontSize: '0.85rem' }}>
                          {grupo.estadoGarantia === 'No pagada' ? (
                            <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                              S/ {garantiaNum.toFixed(2)}
                            </span>
                          ) : (
                            `S/ ${garantiaNum.toFixed(2)}`
                          )}
                        </span>
                      ) : (
                        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>No aplica</span>
                      )}
                    </td>
                    <td style={{ padding: '10px' }}>
                      {grupo.voucherUrl ? (
                        <a href={grupo.voucherUrl} target="_blank" rel="noopener noreferrer" style={{ backgroundColor: '#0284c7', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', textDecoration: 'none', display: 'inline-block' }}>
                          👁️ Ver ↗
                        </a>
                      ) : (
                        <span style={{ color: '#16a34a', fontSize: '0.75rem', fontWeight: 'bold' }}>Gratis / Sin costo</span>
                      )}
                    </td>
                    <td style={{ padding: '10px' }}>
                      {garantiaNum > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                          {grupo.voucherGarantiaUrl ? (
                            <a href={grupo.voucherGarantiaUrl} target="_blank" rel="noopener noreferrer" style={{ backgroundColor: '#d97706', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', textDecoration: 'none', display: 'inline-block' }}>
                              👁️ Ver Garantía ↗
                            </a>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontStyle: 'italic' }}>Sin voucher</span>
                          )}

                          {grupo.estadoGarantia === 'Devuelta' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                              <span style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '3px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                ✅ Garantía Devuelta
                              </span>
                              <button 
                                type="button"
                                onClick={() => retornarGarantiaGrupoGlobal(grupo)}
                                style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                                title="Anular devolución y volver a sumar al total"
                              >
                                ↩️ Anular / Volver a Sumar
                              </button>
                            </div>
                          ) : grupo.estadoGarantia === 'No pagada' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                              <span style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                ❌ Garantía No Pagada
                              </span>
                              <button 
                                type="button"
                                onClick={() => toggleGarantiaNoPagadaGlobal(grupo)}
                                style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                                title="Marcar como pagada y volver a sumar al total"
                              >
                                ✅ Marcar como Pagada
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                              <button 
                                type="button"
                                onClick={() => retornarGarantiaGrupoGlobal(grupo)}
                                style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', cursor: 'pointer' }}
                                title="Devolver garantía y descontar del total acumulado"
                              >
                                🔄 Devolver Garantía
                              </button>
                              <button 
                                type="button"
                                onClick={() => toggleGarantiaNoPagadaGlobal(grupo)}
                                style={{ backgroundColor: '#b91c1c', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                                title="Marcar que no pagaron la garantía para excluirla del total"
                              >
                                ❌ No Pagaron Garantía
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>No aplica</span>
                      )}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <button 
                        onClick={() => liberarGrupoGlobal(grupo.edificioId, grupo.ids)} 
                        style={{ backgroundColor: '#d9534f', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' }}
                        title="Liberar horarios (rechazar voucher o cancelar reserva)"
                      >
                        Liberar
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}